// Mutation hooks for belt profile management (#746).
//
// All writes hit `belt_profiles` and `belt_ranks` directly — RLS in the schema
// migration (#744 / PR #756) gates this to owners on company-owned profiles
// only. No SECURITY DEFINER RPCs needed for #746; the lifecycle mutations on
// student_ranks / belt_promotions land in #747 / #749.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';
import type { BeltProfile, BeltRank, RankDraft } from '../../types/belts';
import { beltsQueryKeys } from './useBelts';

// ============================================================================
// Clone a system template -> a new company-owned profile
// ============================================================================

export interface CloneBeltProfileInput {
  sourceProfileId: string;
  sportId: string;
  name: string;
  description?: string | null;
}

export function useCloneBeltProfile() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (input: CloneBeltProfileInput): Promise<BeltProfile> => {
      if (!companyId) throw new Error('No active company');

      // 1. Insert the new company-owned profile pointing at the template.
      const { data: profile, error: profileErr } = await supabase
        .from('belt_profiles')
        .insert({
          name: input.name,
          description: input.description ?? null,
          company_id: companyId,
          sport_id: input.sportId,
          source_profile_id: input.sourceProfileId,
          is_system: false,
        })
        .select()
        .single();
      if (profileErr) throw profileErr;

      // 2. Load source ranks and bulk-copy them onto the new profile.
      const { data: sourceRanks, error: ranksErr } = await supabase
        .from('belt_ranks')
        .select('name, rank_order, time_required_days, attendance_required')
        .eq('profile_id', input.sourceProfileId)
        .order('rank_order', { ascending: true });
      if (ranksErr) throw ranksErr;

      if (sourceRanks && sourceRanks.length > 0) {
        const { error: copyErr } = await supabase.from('belt_ranks').insert(
          sourceRanks.map((r) => ({
            profile_id: profile.id,
            name: r.name,
            rank_order: r.rank_order,
            time_required_days: r.time_required_days,
            attendance_required: r.attendance_required,
          }))
        );
        if (copyErr) throw copyErr;
      }

      return profile as BeltProfile;
    },
    onSuccess: (profile) => {
      queryClient.invalidateQueries({ queryKey: beltsQueryKeys.profiles() });
      queryClient.invalidateQueries({
        queryKey: beltsQueryKeys.profile(profile.id),
      });
    },
  });
}

// ============================================================================
// Rename / re-describe a company-owned profile
// ============================================================================

export interface UpdateBeltProfileInput {
  profileId: string;
  name?: string;
  description?: string | null;
}

export function useUpdateBeltProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateBeltProfileInput) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name;
      if (input.description !== undefined) patch.description = input.description;

      const { data, error } = await supabase
        .from('belt_profiles')
        .update(patch)
        .eq('id', input.profileId)
        .select()
        .single();
      if (error) throw error;
      return data as BeltProfile;
    },
    onSuccess: (profile) => {
      queryClient.invalidateQueries({ queryKey: beltsQueryKeys.profiles() });
      queryClient.invalidateQueries({
        queryKey: beltsQueryKeys.profile(profile.id),
      });
    },
  });
}

// ============================================================================
// Delete a company-owned profile (CASCADEs to belt_ranks)
// ============================================================================
// FK on student_ranks.current_rank_id is NO ACTION, so this fails if any
// student is still ranked under this profile. The editor UI surfaces a count
// before letting the user attempt deletion (see useBeltProfileRankReferences).

export function useDeleteBeltProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from('belt_profiles')
        .delete()
        .eq('id', profileId);
      if (error) throw error;
      return profileId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: beltsQueryKeys.profiles() });
    },
  });
}

// ============================================================================
// Rank-list save: a single mutation that diffs the editor's draft against
// existing ranks and emits the right set of insert / update / delete calls.
// One mutation simplifies the UI's transactionality story — either the whole
// save succeeds or the user sees one error.
// ============================================================================

export interface SaveRanksInput {
  profileId: string;
  drafts: RankDraft[];
  existingIds: string[]; // ids currently in the DB; any not in drafts get deleted
}

export function useSaveRanks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveRanksInput) => {
      const draftIds = new Set(input.drafts.map((d) => d.id).filter(Boolean) as string[]);
      const toDelete = input.existingIds.filter((id) => !draftIds.has(id));

      // 1. Delete removed ranks first (FK on student_ranks.current_rank_id is
      //    NO ACTION; supabase will surface the error if a rank is still in
      //    use — caller shows the message to the user).
      if (toDelete.length > 0) {
        const { error } = await supabase
          .from('belt_ranks')
          .delete()
          .in('id', toDelete);
        if (error) throw error;
      }

      // 2. Two-phase reorder to avoid colliding with UNIQUE (profile_id,
      //    rank_order): push every existing row to a negative slot first,
      //    then write the target rank_order in the second pass.
      const existingDrafts = input.drafts.filter((d) => d.id);
      if (existingDrafts.length > 0) {
        for (let i = 0; i < existingDrafts.length; i++) {
          const d = existingDrafts[i];
          const { error } = await supabase
            .from('belt_ranks')
            .update({ rank_order: -1 - i })
            .eq('id', d.id!);
          if (error) throw error;
        }
        for (const d of existingDrafts) {
          const { error } = await supabase
            .from('belt_ranks')
            .update({
              name: d.name,
              rank_order: d.rank_order,
              time_required_days: d.time_required_days,
              attendance_required: d.attendance_required,
            })
            .eq('id', d.id!);
          if (error) throw error;
        }
      }

      // 3. Insert brand-new ranks last so they don't fight for rank_order
      //    slots while the existing rows are being shuffled.
      const newDrafts = input.drafts.filter((d) => !d.id);
      if (newDrafts.length > 0) {
        const { error } = await supabase.from('belt_ranks').insert(
          newDrafts.map((d) => ({
            profile_id: input.profileId,
            name: d.name,
            rank_order: d.rank_order,
            time_required_days: d.time_required_days,
            attendance_required: d.attendance_required,
          }))
        );
        if (error) throw error;
      }

      return input.profileId;
    },
    onSuccess: (profileId) => {
      queryClient.invalidateQueries({
        queryKey: beltsQueryKeys.profile(profileId),
      });
      queryClient.invalidateQueries({
        queryKey: beltsQueryKeys.rankReferences(profileId),
      });
    },
  });
}

// ============================================================================
// "Copy criteria between belts" — bulk-copies time_required_days and
// attendance_required from a source rank onto one or more target ranks. Spec
// in #746 calls this out explicitly.
// ============================================================================

export interface CopyCriteriaInput {
  profileId: string;
  source: Pick<BeltRank, 'time_required_days' | 'attendance_required'>;
  targetRankIds: string[];
}

export function useCopyCriteria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CopyCriteriaInput) => {
      if (input.targetRankIds.length === 0) return input.profileId;
      const { error } = await supabase
        .from('belt_ranks')
        .update({
          time_required_days: input.source.time_required_days,
          attendance_required: input.source.attendance_required,
        })
        .in('id', input.targetRankIds);
      if (error) throw error;
      return input.profileId;
    },
    onSuccess: (profileId) => {
      queryClient.invalidateQueries({
        queryKey: beltsQueryKeys.profile(profileId),
      });
    },
  });
}
